import { useTranslation } from '@silospot/i18n/react';
import type { ThreatRow } from '@silospot/shared';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Badge, Card, CardHead, Kpi, KpiGrid, PageHeader, RiskPill } from '../../components/cld';
import { AreaChart } from '../../components/cld/charts';
import { Flag } from '../../components/location';
import { api } from '../../lib/api';
import { fmtHM } from '../../lib/datetime';

const SIGNAL_DOT: Record<string, string> = {
  bot: '#7c3aed',
  headless: '#64748b',
  proxy: '#0891b2',
  datacenter: '#d97706',
  fingerprint: '#dc2626',
  velocity: '#ea580c',
  risk: '#db2777',
};

export function RealtimePage(): ReactElement {
  const { t } = useTranslation('pages');
  const { t: ts } = useTranslation('shell');
  const { id = '' } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['cld-realtime', id],
    queryFn: () => api.getRealtime(id),
    enabled: !!id,
    refetchInterval: 15_000,
  });

  const nf = new Intl.NumberFormat('fr-FR');
  const header = (
    <PageHeader
      title={ts('nav.realtime')}
      sub={t('realtime.last24')}
      actions={
        <Badge tone="fraud" dot>
          {t('fraud.realtime')}
        </Badge>
      }
    />
  );
  if (isLoading || !data) {
    return (
      <div>
        {header}
        <Card>
          <div className="h-64 animate-pulse rounded-lg bg-neutral-bg" />
        </Card>
      </div>
    );
  }

  return (
    <div>
      {header}
      <KpiGrid cols={3}>
        <Kpi label={t('realtime.activeVisitors')} value={nf.format(data.activeVisitors)} />
        <Kpi label={t('realtime.suspectNow')} value={nf.format(data.suspectActive)} />
        <Kpi label={t('signal.bot')} value={nf.format(data.feed.filter((e) => e.isBot).length)} />
      </KpiGrid>
      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1.5fr_1fr]">
        <Card>
          <CardHead title={t('realtime.hourly')} sub={t('realtime.last24')} />
          <AreaChart
            labels={data.hourly.map((h) => fmtHM(h.t))}
            series={[
              {
                name: t('realtime.clean'),
                color: '#2563eb',
                data: data.hourly.map((h) => h.clean),
              },
              {
                name: t('realtime.fraud'),
                color: '#dc2626',
                data: data.hourly.map((h) => h.fraud),
              },
            ]}
          />
        </Card>
        <Card>
          <CardHead
            title={t('realtime.liveFeed')}
            right={
              <Badge tone="fraud" dot>
                live
              </Badge>
            }
          />
          <Feed rows={data.feed} t={t} />
        </Card>
      </div>
    </div>
  );
}

function Feed({
  rows,
  t,
}: {
  rows: ThreatRow[];
  t: (k: string) => string;
}): ReactElement {
  if (rows.length === 0) {
    return <p className="py-10 text-center text-[13px] text-text-3">—</p>;
  }
  return (
    <div className="flex flex-col">
      {rows.slice(0, 10).map((e, i) => (
        <div
          key={e.visitId}
          className={`grid grid-cols-[8px_1fr_auto] items-center gap-3 py-2.5 ${i < Math.min(rows.length, 10) - 1 ? 'border-b border-[#f1f3f6]' : ''}`}
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: SIGNAL_DOT[e.signal] ?? '#98a0ae' }}
          />
          <div className="min-w-0">
            <div className="truncate text-[13px] font-medium text-text">
              {t(`signal.${e.signal}`)}
            </div>
            <div className="truncate font-mono text-[11.5px] text-text-3">
              {e.ip} · <Flag country={e.country} /> {e.country} · {fmtHM(e.arrivedAt)}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <RiskPill value={e.score} small />
            <Badge tone="suspect">{t('action.detected')}</Badge>
          </div>
        </div>
      ))}
    </div>
  );
}
